import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type UserRole = 'admin' | 'operator' | 'worker' | 'supervisor' | 'client';

export interface User {
  id: string;
  login: string;
  /** Отображаемое имя из анкеты роли (клиент — организация, воркер — full_name); у персонала null */
  name: string | null;
  role: UserRole;
  /** Онбординг клиента: карточка организации сохранена хотя бы раз.
   *  Для остальных ролей не используется (undefined = подтверждён). */
  confirmed?: boolean;
}

/** Персонал платформы: видит все сущности; client — тенант, worker — мобильное приложение. */
export const isStaffRole = (role: UserRole): boolean => role === 'admin';

interface UserState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  /** true для персонала (admin) — полный доступ; client — только свои сущности. */
  isStaff: boolean;
  setAuth: (token: string, user: User) => void;
  setConfirmed: (confirmed: boolean) => void;
  logout: () => void;
}

export const useUserStore = create<UserState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      isStaff: false,
      setAuth: (token, user) =>
        set({ token, user, isAuthenticated: true, isStaff: isStaffRole(user.role) }),
      setConfirmed: (confirmed) =>
        set((state) => ({ user: state.user ? { ...state.user, confirmed } : state.user })),
      logout: () => set({ token: null, user: null, isAuthenticated: false, isStaff: false }),
    }),
    {
      name: 'vc-admin-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      // isStaff не храним: пересчитываем из роли при регидрации
      // (сессии, сохранённые до появления флага, остаются корректными)
      merge: (persisted, current) => {
        const saved = (persisted ?? {}) as Partial<UserState>;
        const user = saved.user ?? current.user;
        return {
          ...current,
          ...saved,
          isAuthenticated: saved.isAuthenticated ?? current.isAuthenticated,
          isStaff: user ? isStaffRole(user.role) : false,
        };
      },
    },
  ),
);

export const selectIsAuthenticated = (s: UserState) => s.isAuthenticated;
export const selectToken = (s: UserState) => s.token;
export const selectUser = (s: UserState) => s.user;
export const selectIsStaff = (s: UserState) => s.isStaff;
